import { useEffect, useState } from "react";
import type { NewsDigestTopline } from "@kmitl/shared";

const MAX_TOPLINES = 5;

interface DigestResult {
  toplines: NewsDigestTopline[];
  totalInWindow: number;
  loading: boolean;
}

interface RawDigestResponse {
  topHeadlines?: NewsDigestTopline[];
  totalInWindow?: number;
}

/**
 * Fetches the 7-day news digest from /api/news/digest once on mount.
 * No polling — the digest changes at most daily. Silently returns an
 * empty result if the endpoint is unavailable (503 on Workers runtime).
 */
export function useNewsDigest(apiBase: string): DigestResult {
  const [state, setState] = useState<DigestResult>({
    toplines: [],
    totalInWindow: 0,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${apiBase}/api/news/digest?period=7d`);
        if (!r.ok) {
          if (!cancelled) setState((s) => ({ ...s, loading: false }));
          return;
        }
        const raw = (await r.json()) as RawDigestResponse;
        if (!cancelled) {
          setState({
            toplines: (raw.topHeadlines ?? []).slice(0, MAX_TOPLINES),
            totalInWindow: raw.totalInWindow ?? 0,
            loading: false,
          });
        }
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [apiBase]);

  return state;
}
